import React, { useEffect, useRef } from 'react';

import { ContentDeliverable, CourseGating, CourseResource, CourseResourceType, CourseType, DeliverableType, ProctorTypes, TransactionApi } from 'apis/TransactionApi';
import { TransactionData, TransactionDetailsContext } from 'pages/access_codes/context';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import GMetrixDialogShell from 'components/GMetrixDialogShell/GMetrixDialogShell';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { Alert, Col, Collapse, Form, ListGroup, Row } from 'react-bootstrap';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { Variant } from 'react-bootstrap/esm/types';
import { TabsContext } from 'components/Tabs/Tabs';
import { GroupData } from 'pages/groups/context';
import { useContext } from 'components/Tabs';
import { useSuperState } from 'hooks';
import moment from 'moment';

// Define the data structure
export interface DeliverableContentTreeNode {
    actions?: NodeActions[];
    items?: DeliverableContentTreeNode[];
    text: string;
}

interface NodeActions {
    action: () => any;
    icon: string;
    variant: Variant;
}

interface DefaultSettingsForm {
    courseGateType: CourseGating | undefined;
    deliverableTypeId: number;
    enableQuestionReview: boolean;
    endDate: Date | null;
    id: number;
    isProctored: boolean;
    maxAttempts: number;
    name: string;
    proctoring: ProctorTypes | undefined;
    startDate: Date | undefined;
    testMode: boolean;
    trainingMode: boolean;
    transactionId: number;
}

interface TreeNodeProps {
    courseGatingTypes: CourseGating[];
    currentContext: React.Context<TabsContext<TransactionData>> | React.Context<TabsContext<GroupData>>;
    node: DeliverableContentTreeNode;
    totalListItemCount: number;

}

// Server-provided expiration (DueDate) values are stored at the END of the day
// while start dates are at midnight. Take the value's LOCAL calendar day — the
// day the user actually sees, exactly how a midnight start date already
// resolves — then anchor at UTC noon so the picker's UTC-based display keeps
// that same day instead of rolling an end-of-day expiration forward.
const toNoonUtcDay = (value: Date | string | null | undefined): Date | undefined => {

    if (value === null || value === undefined || value === '') return undefined;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;

    return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12, 0, 0));

};

const TreeNode = (props: TreeNodeProps) => {

    const { courseGatingTypes, currentContext, node, totalListItemCount } = props;

    const [state, setState] = useSuperState({
        currentContentIndex: 0,
        currentProctorTypes: [] as ProctorTypes[],
        editSettingsObject: {
            id: 0,
            name: '',
            courseGateType: undefined,
            deliverableTypeId: 0,
            enableQuestionReview: false,
            endDate: null,
            isProctored: false,
            maxAttempts: 0,
            proctoring: undefined,
            startDate: undefined,
            testMode: true,
            trainingMode: true,
            transactionId: 0
        } as DefaultSettingsForm,
        error: false,
        loading: false,
        open: totalListItemCount <= 500,
        showEditModal: false
    });
    const { currentContentIndex, currentProctorTypes, editSettingsObject, error, loading, open, showEditModal } = state;

    const hasChildren = !!node?.items?.length;
    const { formatMessage } = useIntl();
    const { pageContext, update } = useContext(currentContext);

    const gmetrixDialogShellRef = useRef<any>();
    const { clearAllToasts, createToast } = useNotification();

    useEffect(() => {

        if (showEditModal) return;
        clearAllToasts();

    }, [showEditModal]);

    const clearError = () => setState({ error: null });

    const setEditSettingsObject = (editSettingsObject: DefaultSettingsForm) => setState({ editSettingsObject });

    const handleLoadIcon = (deliverable: ContentDeliverable) => {

        let className = '';
        let deliverableTypeText = '';
        switch (true) {

            case deliverable.DeliverableTypeId === DeliverableType.Test:
                if (deliverable.IsCustomTest) {

                    className = 'fa-light fa-file-pen text-warning';
                    deliverableTypeText = formatMessage({ id: 'practice_test.11b3e14c', defaultMessage: 'Practice Test' });

                } else {

                    className = 'fa-light fa-ballot-check text-primary';
                    deliverableTypeText = formatMessage({ id: 'test.051b8cd1', defaultMessage: 'Test' });

                }
                break;
            case deliverable.DeliverableTypeId === DeliverableType.Book:
                className = 'fa-light fa-book text-success';
                deliverableTypeText = formatMessage({ id: 'ebook.5d34c24a', defaultMessage: 'EBook' });
                break;
            case deliverable.DeliverableTypeId === DeliverableType.Course:
                className = 'fa-light fa-rectangle-history purple';
                deliverableTypeText = formatMessage({ id: 'course.790646f3', defaultMessage: 'Course' });
                break;
            default:
                break;

        }
        return <i className={`me-2 kendo-tree-view__list-item__icon ${className}`} title={deliverableTypeText}></i>;

    };

    // Eventually these should be removed from this file and placed in the Classroom Content file
    const getCourseResourceIcon = (courseResourceTypeId: CourseResourceType): { color: string; icon: string } => {

        switch (courseResourceTypeId) {

            case CourseResourceType.PDF:
                return { color: 'C4493E', icon: 'fa-file-pdf' };

            case CourseResourceType.WordDocument:
                return { color: '4371BB', icon: 'fa-file-word' };

            case CourseResourceType.PowerpointDocument:
                return { color: 'EB8D36', icon: 'fa-file-powerpoint' };

            case CourseResourceType.ExcelDocument:
                return { color: '01B555', icon: 'fa-file-excel' };

            case CourseResourceType.ZipFile:
                return { color: 'F7CE3B', icon: 'fa-file-zip' };

            case CourseResourceType.WebContent:
            default:
                return { color: 'BABABA', icon: 'fa-file' };

        }

    };

    const handleResources = async (selectedDeliverable: any) => {

        const downloadedLinks: any[] = [];
        const instructorResources = await TransactionApi.GetCourseResourcesByCourseId(selectedDeliverable?.Id, 1, -1);

        gmetrixDialogShellRef.current.create(
            'Instructor Resources',
            (
                <>
                    {instructorResources.length > 0 ?
                        (
                            <>
                                <div className="d-flex justify-content-end mb-4">

                                    <GMetrixButton
                                        onClick={() => {

                                            downloadedLinks.forEach((url: any) => {

                                                const iframe = document.createElement('iframe');
                                                iframe.style.visibility = 'collapse';
                                                document.body.append(iframe);

                                                iframe?.contentDocument?.write(
                                                    // eslint-disable-next-line no-useless-escape
                                                    `<form action="${url.replace(/\"/g, '"')}" method="GET"></form>`
                                                );
                                                iframe?.contentDocument?.forms[0].submit();

                                                setTimeout(() => iframe.remove(), 20000);

                                            });

                                        }}
                                        iconClass="fa-regular fa-download"
                                        variant="primary"
                                    >
                                        <FormattedMessage defaultMessage="Download All" id="download_all.4d3d4285" />
                                    </GMetrixButton>
                                </div>

                                {instructorResources.map((instructorResource: CourseResource) => {

                                    downloadedLinks.push(instructorResource.URL);
                                    const courseResourceType = getCourseResourceIcon(instructorResource.ResourceTypeId);

                                    return (

                                        <div
                                            className="d-flex justify-content-between flex-nowrap mb-2"
                                            key={instructorResource.Id}
                                        >
                                            <div className="d-flex">
                                                <i
                                                    style={{
                                                        color: `#${courseResourceType.color}`
                                                    }}
                                                    className={`content-settings__icon fa-regular ${courseResourceType.icon}`}
                                                ></i>
                                                <div className="ms-3 fw-bold">
                                                    {instructorResource.Name}
                                                </div>
                                            </div>

                                            <GMetrixButton
                                                onClick={() => {

                                                    const link = document.createElement('a');
                                                    link.href = instructorResource.URL;
                                                    link.download = instructorResource.Name; // Specifies the default filename for the download
                                                    document.body.appendChild(link); // Append to body (can be hidden)
                                                    link.click(); // Programmatically click the link
                                                    document.body.removeChild(link); // Remove the link after clicking

                                                }}
                                                iconClass="fa-regular fa-download"
                                                variant="outline-primary"
                                            >
                                                <FormattedMessage defaultMessage="Download" id="download.5e1e7483" />
                                            </GMetrixButton>
                                        </div>
                                    );

                                })}
                            </>
                        )
                        : instructorResources.length === 0 ?
                            (
                                <div className="py-3 text-center content-settings__no-resources">
                                    <FormattedMessage defaultMessage="No Instructor Resources to show" id="no_instructor_resources_to_show.ffbbbbb3" />
                                    .
                                </div>
                            )
                            :
                            (
                                <div className="py-3">
                                    <GMetrixLoader />
                                </div>
                            )
                    }
                </>
            ),
            {
                buttonProps: {
                    buttonText: formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' }),
                    hideCancel: true,
                    variant: 'primary'
                },
                size: 'lg'
            }
        );

    };

    const handleEditSettings = async (node: ContentDeliverable) => {

        if (pageContext?.Data.content) {

            for (let index = 0; index < pageContext?.Data.content.length; index++) {

                const element = pageContext?.Data.content[index];
                if (element.Id === node.Id) {

                    setState({ currentContentIndex: index });
                    break;

                }

            }

        }

        // check if proctor types needs a call
        let proctor: ProctorTypes = { Description: 'No Description', Id: 1, Name: 'Default' };

        if (node.DeliverableTypeId === DeliverableType.Book) {

            try {

                const result = await TransactionApi.GetAllProctorTypes(node.Id);
                if (result) {

                    setState({ currentProctorTypes: result });
                    proctor = result[0];

                }

            } catch (e) {

                console.error(e);

            }

        }

        if (node.MaxAttempts === undefined && pageContext?.Data.transactionDetails?.NumOfTests !== undefined) {

            node.MaxAttempts = pageContext?.Data.transactionDetails?.NumOfTests;

        }

        setEditSettingsObject({
            id: node.Id,
            name: node.Name,
            courseGateType: courseGatingTypes[node.CourseGateType ?? 0],
            deliverableTypeId: node.DeliverableTypeId,
            enableQuestionReview: node.AllowReview,
            endDate: toNoonUtcDay(node.ExpirationDate) ?? null,
            isProctored: node.IsProctored,
            maxAttempts: node.MaxAttempts,
            proctoring: proctor,
            startDate: toNoonUtcDay(node.StartDate),
            testMode: node.TestingMode,
            trainingMode: node.TrainingMode,
            transactionId: node.TransactionId
        });

        setState({ showEditModal: true });

    };

    const handleDelete = async (node: any) => {

        // this currently deletes the content item but does not refresh the list
        if (pageContext?.Data.content && pageContext.Data.transactionDetails) {

            await TransactionApi.SyncXEDGroup(
                pageContext.Data.transactionDetails.Id,
                -1,
                -1,
                true,
                pageContext?.Data.content
                    .filter(
                        content => (content.DeliverableTypeId === DeliverableType.Course ||
                            content.DeliverableTypeId === DeliverableType.XedCourse) &&
                            content.CourseType === CourseType.XEDCourse
                    )
                    .map(course => course.Id.toString())
            );

        }

        if (pageContext?.Data.transactionDetails) {

            createToast(formatMessage({ id: 'the_content_was_saved_successfully.6c899203', defaultMessage: 'The content was saved successfully' }), 'success');

            await TransactionApi.DeleteOneContentFromTransaction(
                pageContext.Data.transactionDetails.Id,
                node.DeliverableTypeId,
                node.Id
            )
                .catch(console.error);

            const assignedContent = await TransactionApi.GetLimitedContentByTransactionId(pageContext.Data.transactionDetails.Id, pageContext.Data.transactionDetails.PartnerId)
                .catch(console.error);

            if (assignedContent) {

                update({ content: assignedContent });

            }

        }

    };

    const handleSaveContentSettings = async () => {

        try {

            setState({ loading: true });

            // save the content with editSettingsObject
            if (((node as ContentDeliverable).DeliverableTypeId === DeliverableType.XedCourse || (node as ContentDeliverable).DeliverableTypeId === DeliverableType.Test)) {

                if (editSettingsObject.maxAttempts === undefined || Number.isNaN(editSettingsObject.maxAttempts)) {

                    createToast(formatMessage({ id: 'max_attempts_cannot_be_left_blank.0779b9aa', defaultMessage: 'Max Attempts cannot be left blank.' }), 'danger');
                    return;

                }

            }

            const result = await TransactionApi.UpdateSingleContentSettings([{
                AllowReview: editSettingsObject.enableQuestionReview,
                CourseGateType: editSettingsObject.courseGateType?.Id,
                DeliverableTypeId: editSettingsObject.deliverableTypeId,
                ExpirationDate: editSettingsObject.endDate,
                Id: editSettingsObject.id,
                IsProctored: editSettingsObject.isProctored,
                MaxAttempts: editSettingsObject.maxAttempts,
                ProctorTypesAllowed: [editSettingsObject.proctoring?.Id ?? 0],
                StartDate: editSettingsObject.startDate,
                TestingMode: editSettingsObject.testMode,
                TrainingMode: editSettingsObject.trainingMode,
                TransactionId: editSettingsObject.transactionId
            } as ContentDeliverable]);

            if (result.Done) {

                if (pageContext?.Data.content) {

                    // find the content piece then replace it
                    const copyOfContent = [...pageContext.Data.content];
                    let copyOfContentPiece = { ...pageContext.Data.content[currentContentIndex] };
                    copyOfContentPiece = {
                        ...copyOfContentPiece,
                        AllowReview: editSettingsObject.enableQuestionReview,
                        CourseGateType: editSettingsObject.courseGateType?.Id,
                        DeliverableTypeId: editSettingsObject.deliverableTypeId,
                        ExpirationDate: editSettingsObject.endDate,
                        Id: editSettingsObject.id,
                        IsProctored: editSettingsObject.isProctored,
                        MaxAttempts: editSettingsObject.maxAttempts,
                        ProctorTypesAllowed: [editSettingsObject.proctoring?.Id ?? 0],
                        StartDate: editSettingsObject.startDate,
                        TestingMode: editSettingsObject.testMode,
                        TrainingMode: editSettingsObject.trainingMode,
                        TransactionId: editSettingsObject.transactionId
                    };
                    copyOfContent[currentContentIndex] = copyOfContentPiece;

                    update({ content: copyOfContent });
                    createToast(formatMessage({ id: 'name_was_edited_successfully.2b8d4098', defaultMessage: '{name} was edited successfully' }, { name: editSettingsObject.name }), 'success');

                }

            } else {

                throw new Error(result.Message ?? 'A server error occured');

            }

            setState({
                loading: false,
                showEditModal: false
            });

        } catch (e){

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const displayModal = (node: ContentDeliverable) => {

        // We want to remove date inputs on access codes but not classrooms
        let showDateInputs = true;
        if (currentContext === TransactionDetailsContext) {

            showDateInputs = false;

        }

        switch (node.DeliverableTypeId) {

            case DeliverableType.XedCourse:

                return (
                    <>
                        <h5 className="mb-3">
                            {node.Name}
                        </h5>

                        {showDateInputs && (
                            <Row className="mb-3">
                                <Col md={6}>
                                    <GMetrixDatePicker
                                        onChange={date => {

                                            if (date === undefined) {

                                                setEditSettingsObject({ ...editSettingsObject, startDate: undefined });
                                                return;

                                            }
                                            const startDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                            setEditSettingsObject({ ...editSettingsObject, startDate: startDate });

                                        }}
                                        disabled={loading}
                                        infoTitle={formatMessage({ id: 'sets_the_start_date_of_the_content.f79467c1', defaultMessage: 'Sets the start date of the content' })}
                                        label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                        value={editSettingsObject?.startDate}
                                    />
                                </Col>
                                <Col md={6}>
                                    <GMetrixDatePicker
                                        onChange={date => {

                                            if (date === undefined) {

                                                setEditSettingsObject({ ...editSettingsObject, endDate: null });
                                                return;

                                            }
                                            const endDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                            setEditSettingsObject({ ...editSettingsObject, endDate: endDate });

                                        }}
                                        disabled={loading}
                                        infoTitle={formatMessage({ id: 'sets_the_end_date_of_the_content.542963b3', defaultMessage: 'Sets the end date of the content' })}
                                        label={formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' })}
                                        value={editSettingsObject?.endDate}
                                    />
                                </Col>
                            </Row>
                        )}

                        <Row className="mb-3">
                            <Col md={6}>
                                <GMetrixInput
                                    disabled={loading}
                                    label={formatMessage({ id: 'max_attempts.3822990c', defaultMessage: 'Max Attempts' })}
                                    onChange={e => setEditSettingsObject({ ...editSettingsObject, maxAttempts: parseInt(e.target.value) })}
                                    type="number"
                                    value={editSettingsObject?.maxAttempts}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">

                            <Col md={6}>
                                {currentProctorTypes.length > 1 ? (
                                    <GMetrixDropdown
                                        dataItemKey="Id"
                                        dataItems={currentProctorTypes}
                                        disabled={loading}
                                        label={formatMessage({ id: 'proctoring.4139a0f9', defaultMessage: 'Proctoring' })}
                                        onChange={proctor => setEditSettingsObject({ ...editSettingsObject, proctoring: proctor })}
                                        textField="Name"
                                        value={editSettingsObject.proctoring}
                                    >

                                    </GMetrixDropdown>
                                )
                                    : (
                                        <GMetrixCheckInput
                                            checked={editSettingsObject.isProctored}
                                            disabled={loading}
                                            infoTitle={formatMessage({ id: 'when_enabled_a_proctor_must_sign_in_befo.fe1f9085', defaultMessage: 'When enabled, a proctor must sign-in before the test begins' })}
                                            label={formatMessage({ id: 'proctoring.4139a0f9', defaultMessage: 'Proctoring' })}
                                            onChange={e => setEditSettingsObject({ ...editSettingsObject, isProctored: e.target.checked })}
                                            type={'switch'}
                                        />
                                    )}
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <GMetrixCheckInput
                                    defaultChecked
                                    checked={editSettingsObject.enableQuestionReview}
                                    disabled={loading}
                                    infoTitle={formatMessage({ id: 'adds_question_review.5e6fb3bf', defaultMessage: 'Adds question review' })}
                                    label={formatMessage({ id: 'question_review.8f63f046', defaultMessage: 'Question Review' })} onChange={e => setEditSettingsObject({ ...editSettingsObject, enableQuestionReview: e.target.checked })}
                                    type="checkbox"
                                />
                            </Col>
                        </Row>
                    </>
                );

            case DeliverableType.Test:

                {/* Default Settings */}
                return (
                    <>
                        <h5 className="mb-3">
                            {node.Name}
                        </h5>

                        {showDateInputs && (
                            <Row className="mb-3">
                                <Col md={6}>
                                    <GMetrixDatePicker
                                        onChange={date => {

                                            if (date === undefined) {

                                                setEditSettingsObject({ ...editSettingsObject, startDate: undefined });
                                                return;

                                            }
                                            const startDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                            setEditSettingsObject({ ...editSettingsObject, startDate: startDate });

                                        }}
                                        infoTitle={formatMessage({ id: 'sets_the_start_date_of_the_content.f79467c1', defaultMessage: 'Sets the start date of the content' })}
                                        label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                        readOnly={loading}
                                        value={editSettingsObject?.startDate}
                                    />
                                </Col>
                                <Col md={6}>
                                    <GMetrixDatePicker
                                        onChange={date => {

                                            if (date === undefined) {

                                                setEditSettingsObject({ ...editSettingsObject, endDate: null });
                                                return;

                                            }
                                            const endDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                            setEditSettingsObject({ ...editSettingsObject, endDate: endDate });

                                        }}
                                        infoTitle={formatMessage({ id: 'sets_the_end_date_of_the_content.542963b3', defaultMessage: 'Sets the end date of the content' })}
                                        label={formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' })}
                                        readOnly={loading}
                                        value={editSettingsObject?.endDate}
                                    />
                                </Col>
                            </Row>
                        )}

                        <Row className="mb-3">
                            <Col md={6}>
                                <GMetrixInput
                                    onChange={e => {

                                        let numberInput = parseInt(e.target.value);
                                        if (pageContext?.Data.transactionDetails && numberInput > pageContext?.Data.transactionDetails?.NumOfTests) {

                                            numberInput = pageContext.Data.transactionDetails.NumOfTests;

                                        }
                                        setEditSettingsObject({ ...editSettingsObject, maxAttempts: numberInput });

                                    }}
                                    disabled={loading}
                                    label={formatMessage({ id: 'max_attempts.3822990c', defaultMessage: 'Max Attempts' })}
                                    max={pageContext?.Data.transactionDetails?.NumOfTests}
                                    type="number"
                                    value={editSettingsObject?.maxAttempts}
                                />
                            </Col>
                            <Col md={6}>
                                <GMetrixDropdown
                                    onChange={mode => {

                                        switch (mode.name) {

                                            case 'Training':
                                                setEditSettingsObject({ ...editSettingsObject, testMode: false, trainingMode: true });
                                                break;
                                            case 'Testing':
                                                setEditSettingsObject({ ...editSettingsObject, testMode: true, trainingMode: false });
                                                break;
                                            case 'Both':
                                            default:
                                                setEditSettingsObject({ ...editSettingsObject, testMode: true, trainingMode: true });
                                                break;

                                        }

                                    }}
                                    dataItemKey="id"
                                    dataItems={[{ id: 1, name: 'Training' }, { id: 2, name: 'Testing' }, { id: 3, name: 'Both' }]}
                                    disabled={loading}
                                    infoTitle={formatMessage({ id: 'training_unlimited_time_with_detailed_fe.cbfaabb1', defaultMessage: 'Training - Unlimited time with detailed feedback Testing - Timed with no detailed feedback' })}
                                    label={formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}
                                    textField="name"
                                    value={editSettingsObject.trainingMode && editSettingsObject.testMode ? { id: 3, name: 'Both' } : editSettingsObject.trainingMode ? { id: 1, name: 'Training' } : { id: 2, name: 'Testing' }}
                                />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                {currentProctorTypes.length > 1 ? (
                                    <GMetrixDropdown
                                        dataItemKey="Id"
                                        dataItems={currentProctorTypes}
                                        disabled={loading}
                                        label={formatMessage({ id: 'proctoring.4139a0f9', defaultMessage: 'Proctoring' })}
                                        onChange={proctor => setEditSettingsObject({ ...editSettingsObject, proctoring: proctor })}
                                        textField="Name"
                                        value={editSettingsObject.proctoring}
                                    >
                                    </GMetrixDropdown>
                                )
                                    : (
                                        <GMetrixCheckInput
                                            checked={editSettingsObject.isProctored}
                                            disabled={loading}
                                            infoTitle={formatMessage({ id: 'when_enabled_a_proctor_must_sign_in_befo.fe1f9085', defaultMessage: 'When enabled, a proctor must sign-in before the test begins' })}
                                            label={formatMessage({ id: 'proctoring.4139a0f9', defaultMessage: 'Proctoring' })}
                                            onChange={e => setEditSettingsObject({ ...editSettingsObject, isProctored: e.target.checked })}
                                            type={'switch'}
                                        />
                                    )}
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <GMetrixCheckInput
                                    defaultChecked
                                    checked={editSettingsObject.enableQuestionReview}
                                    disabled={loading}
                                    infoTitle={formatMessage({ id: 'adds_question_review.5e6fb3bf', defaultMessage: 'Adds question review' })}
                                    label={formatMessage({ id: 'question_review.8f63f046', defaultMessage: 'Question Review' })}
                                    onChange={e => setEditSettingsObject({ ...editSettingsObject, enableQuestionReview: e.target.checked })}
                                    type="checkbox"
                                />
                            </Col>
                        </Row>
                    </>
                );
            case DeliverableType.Course:

                {/* Default Settings */}
                return (
                    <>
                        <h5 className="mb-3">
                            {node.Name}
                        </h5>

                        {showDateInputs && (
                            <Row className="mb-3">
                                <Col md={6}>
                                    <GMetrixDatePicker
                                        onChange={date => {

                                            if (date === undefined) {

                                                setEditSettingsObject({ ...editSettingsObject, startDate: undefined });
                                                return;

                                            }
                                            const startDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                            setEditSettingsObject({ ...editSettingsObject, startDate: startDate });

                                        }}
                                        infoTitle={formatMessage({ id: 'sets_the_start_date_of_the_content.f79467c1', defaultMessage: 'Sets the start date of the content' })}
                                        label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                        readOnly={loading}
                                        value={editSettingsObject?.startDate}
                                    />
                                </Col>
                                <Col md={6}>
                                    <GMetrixDatePicker
                                        onChange={date => {

                                            if (date === undefined) {

                                                setEditSettingsObject({ ...editSettingsObject, endDate: null });
                                                return;

                                            }
                                            const endDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                            setEditSettingsObject({ ...editSettingsObject, endDate: endDate });

                                        }}
                                        infoTitle={formatMessage({ id: 'sets_the_end_date_of_the_content.542963b3', defaultMessage: 'Sets the end date of the content' })}
                                        label={formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' })}
                                        readOnly={loading}
                                        value={editSettingsObject?.endDate}
                                    />
                                </Col>
                            </Row>
                        )}

                        <Row className="mb-3">
                            <Col md={6}>
                                <GMetrixDropdown
                                    dataItemKey="Id"
                                    dataItems={courseGatingTypes}
                                    disabled={loading}
                                    infoTitle={formatMessage({ id: 'students_must_meet_the_selected_criteria.d4d34c5c', defaultMessage: 'Students must meet the selected criteria to progress through the course' })}
                                    label={formatMessage({ id: 'course_gating.10e89741', defaultMessage: 'Course Gating' })}
                                    onChange={type => setEditSettingsObject({ ...editSettingsObject, courseGateType: type })}
                                    textField="Name"
                                    value={editSettingsObject.courseGateType}
                                >
                                </GMetrixDropdown>
                            </Col>
                        </Row>
                    </>
                );
            case DeliverableType.Book:

                // dont show a modal for books
                if (showDateInputs) return;

                {/* Default Settings */}
                return (
                    <>
                        <h5 className="mb-3">
                            {node.Name}
                        </h5>

                        <Row className="mb-3">
                            <Col md={6}>
                                <GMetrixDatePicker
                                    onChange={date => {

                                        if (date === undefined) {

                                            setEditSettingsObject({ ...editSettingsObject, startDate: undefined });
                                            return;

                                        }
                                        const startDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                        setEditSettingsObject({ ...editSettingsObject, startDate: startDate });

                                    }}
                                    infoTitle={formatMessage({ id: 'sets_the_start_date_of_the_content.f79467c1', defaultMessage: 'Sets the start date of the content' })}
                                    label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                    readOnly={loading}
                                    value={editSettingsObject?.startDate}
                                />
                            </Col>
                            <Col md={6}>
                                <GMetrixDatePicker
                                    onChange={date => {

                                        if (date === undefined) {

                                            setEditSettingsObject({ ...editSettingsObject, endDate: null });
                                            return;

                                        }
                                        const endDate = moment(date as any, 'YYYY-MM-DD').toDate();
                                        setEditSettingsObject({ ...editSettingsObject, endDate: endDate });

                                    }}
                                    infoTitle={formatMessage({ id: 'sets_the_end_date_of_the_content.542963b3', defaultMessage: 'Sets the end date of the content' })}
                                    label={formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' })}
                                    readOnly={loading}
                                    value={editSettingsObject?.endDate}
                                />
                            </Col>
                        </Row>
                    </>
                );
            default:
                return <FormattedMessage defaultMessage="Unknown Modal Type" id="unknown_modal_type.d1031357" />;

        }

    };

    return (
        <>

            <ListGroup.Item className="border-0">
                <div className="d-flex justify-content-between align-items-start">
                    <div
                        onClick={() => hasChildren && setState({ open: !open })}
                        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                    >
                        {hasChildren ? (
                            <>
                                {open ? '▼' : '▶'}
                                {' '}
                                {node.text}
                            </>
                        ) : (
                            <>
                                {handleLoadIcon(node as any)}
                                {node.text}
                            </>
                        )}
                    </div>

                    {!hasChildren && (
                        <div>
                            {((node as ContentDeliverable).DeliverableTypeId === DeliverableType.Book || (node as ContentDeliverable).DeliverableTypeId === DeliverableType.Course) && (
                                <GMetrixButton className="me-2" iconClass="fa-regular fa-download" infoTitle={'Download ' + formatMessage({ id: 'teacher_resources.0f7b828f', defaultMessage: 'Teacher Resources' })} onClick={() => handleResources(node)} variant="outline-primary">
                                    <FormattedMessage defaultMessage="Resources" id="resources.73e9320e" />
                                </GMetrixButton>
                            )}
                            <GMetrixButton className="me-2" iconClass="fa-regular fa-pencil" infoTitle={formatMessage({ id: 'edit.00978daf', defaultMessage: 'Edit' })} onClick={() => handleEditSettings(node as any)} variant="outline-primary">
                            </GMetrixButton>
                            <GMetrixButton iconClass="fa-regular fa-trash" infoTitle={formatMessage({ id: 'remove.d34ac1e7', defaultMessage: 'Remove' })} onClick={() => handleDelete(node)} variant="outline-danger">
                            </GMetrixButton>
                        </div>
                    )}
                </div>

                {hasChildren && (
                    <Collapse in={open}>
                        <div className="ms-3 mt-2">
                            <ListGroup variant="flush">
                                {node.items!.map((child, index) => (
                                    <TreeNode courseGatingTypes={courseGatingTypes} currentContext={currentContext} key={index} node={child} totalListItemCount={totalListItemCount} />
                                ))}
                            </ListGroup>
                        </div>
                    </Collapse>
                )}
            </ListGroup.Item>

            <GMetrixDialogShell ref={gmetrixDialogShellRef} />
            <GMetrixDialog
                actionButtonClick={handleSaveContentSettings}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                dialogLabel={formatMessage({ id: 'edit_settings.73cda8dc', defaultMessage: 'Edit Settings' })}
                disabled={loading}
                onCancel={() => setState({ showEditModal: false })}
                show={showEditModal}
                size="lg"
            >
                <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                    {error}
                </Alert>
                <Form>
                    {displayModal(node as ContentDeliverable)}
                </Form>
            </GMetrixDialog>

        </>
    );

};

interface TreeViewProps {
    courseGatingTypes: CourseGating[];
    currentContext: React.Context<TabsContext<TransactionData>> | React.Context<TabsContext<GroupData>>;
    data: DeliverableContentTreeNode[];
    totalListItemCount: number;
}

export function DeliverableContentTreeView({ courseGatingTypes, currentContext, data, totalListItemCount }: TreeViewProps) {

    return (
        <ListGroup style={{ maxHeight: '60vh', overflowY: 'scroll' }} variant="flush">
            {data.map((node, index) => (
                <TreeNode courseGatingTypes={courseGatingTypes} currentContext={currentContext} key={index} node={node} totalListItemCount={totalListItemCount} />
            ))}
        </ListGroup>
    );

}
